import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  Box,
  IconButton,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Divider,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import { fetchContactGroups } from "../../redux/Slice/Contacts/contactGroupsSlice";
import { fetchTemplates } from "../../redux/Slice/Templates/templateSlice";
import {
  createBulkMessageCampaign,
  updateBulkMessageCampaign,
  cancelBulkMessageCampaign,
} from "../../redux/Slice/bulkMessageCampaignSlice";
import AddDatasetPanel from "../../components/Broadcasts/AddDatasetPanel";
import EditDatasetPanel from "../../components/Broadcasts/EditDatasetPanel";
import WhatsAppTemplatePreview from "../../components/Broadcasts/WhatsAppTemplatePreview";
import TemplateVariablesForm from "../../components/Broadcasts/TemplateVariablesForm";
import SendBroadcastModal from "../../components/Broadcasts/SendBroadcastModal";

const ADD_DATASET_VALUE = "__add_dataset__";

// Matches the backend's cancelCampaign guard (bulk.campaign.js).
const CANCELLABLE_STATUSES = new Set(["PENDING", "SCHEDULED", "IN_PROGRESS"]);
// Matches the backend's updateCampaign isDraftEdit guard — dataset/template/
// customizations stay editable until the campaign actually starts sending.
const CONTENT_EDITABLE_STATUSES = new Set(["PENDING", "SCHEDULED"]);

// Maps a picked contact-field token to the backend's per-recipient resolver
// (bulk.payload.js only supports these two "from_contact" sources today).
// $contact.business_name / $contact.email have no resolver yet, so they're
// saved as literal text and won't be substituted per recipient until the
// backend supports them.
const CONTACT_TOKEN_TO_FROM_CONTACT = {
  "$contact.name": "name",
  "$contact.phone_number": "phone",
};

// Matches the backend's resendCompletedCampaign date-suffix format
// (bulk.campaign.js's formatBroadcastDateSuffix) so both flows read the same.
const formatBroadcastDateSuffix = (date) =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const buildCustomizations = (variables) => {
  const customizations = {};
  Object.entries(variables).forEach(([key, value]) => {
    if (!value) return;
    const fromContact = CONTACT_TOKEN_TO_FROM_CONTACT[value];
    customizations[key] = fromContact ? { from_contact: fromContact } : value;
  });
  return customizations;
};

const FROM_CONTACT_TO_TOKEN = {
  name: "$contact.name",
  phone: "$contact.phone_number",
};

// Inverse of buildCustomizations — turns a saved campaign's customizations
// back into the token strings TemplateVariablesForm's fields understand.
const reverseCustomizations = (customizations) => {
  const variables = {};
  Object.entries(customizations || {}).forEach(([key, value]) => {
    if (value && typeof value === "object" && value.from_contact) {
      variables[key] = FROM_CONTACT_TO_TOKEN[value.from_contact] || "";
    } else if (value != null) {
      variables[key] = value;
    }
  });
  return variables;
};

function CreateBroadcastPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { accountid } = useParams();

  // Opened from the broadcast list ("Name" click) — the list already carries
  // the full campaign document (getCampaigns returns every field), so this
  // page can pre-fill entirely client-side without a dedicated fetch-by-id call.
  const existingCampaign = location.state?.campaign || null;
  const initialSegmentId =
    existingCampaign?.contactGroupId && typeof existingCampaign.contactGroupId === "object"
      ? existingCampaign.contactGroupId._id
      : "";
  const initialTemplateId = existingCampaign?.templateId || "";
  const initialTemplateVariables = useMemo(
    () => reverseCustomizations(existingCampaign?.customizations),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [broadcastName, setBroadcastName] = useState(
    existingCampaign?.campaignName || location.state?.name || "Untitled Broadcast"
  );
  const [editingName, setEditingName] = useState(false);

  // Carried from AddBroadcastModal (new broadcast) or the saved campaign
  // (reopened draft) — applied to every contact in the selected dataset on
  // save (see persistCampaign below / bulk.campaign.js's applyLabelToContacts).
  const broadcastLabel = existingCampaign?.label ?? location.state?.label ?? null;

  const { groups } = useSelector((state) => state.contactGroups);
  const [selectedSegmentId, setSelectedSegmentId] = useState(initialSegmentId);
  const [addingDataset, setAddingDataset] = useState(false);

  const { templates } = useSelector((state) => state.template);
  const [addingMessage, setAddingMessage] = useState(Boolean(initialTemplateId));
  const [selectedTemplateId, setSelectedTemplateId] = useState(initialTemplateId);
  const [templateFieldsComplete, setTemplateFieldsComplete] = useState(false);
  const [templateVariables, setTemplateVariables] = useState({});

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // The campaign this page's Save button targets — null until the first
  // successful save (or already set if opened from an existing broadcast).
  // Every save after that updates this same campaign instead of creating a new one.
  const [campaignId, setCampaignId] = useState(existingCampaign?._id || null);
  // Tracks whether content (dataset/template/variables) is still editable —
  // the backend only allows that while PENDING. A freshly-created campaign
  // starts PENDING; an existing SCHEDULED/IN_PROGRESS one opened from the
  // list has its content locked, but its schedule time can still change.
  const [campaignStatus, setCampaignStatus] = useState(existingCampaign?.status || "PENDING");

  useEffect(() => {
    dispatch(fetchContactGroups());
    dispatch(fetchTemplates());
  }, [dispatch]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g._id === selectedSegmentId) || null,
    [groups, selectedSegmentId]
  );

  // Only approved templates can actually be sent — same rule used elsewhere
  // (BulkMessageHistoryModal.jsx, ContactGroupDetails.jsx template picker).
  const approvedTemplates = useMemo(
    () => templates.filter((t) => t.status === "APPROVED"),
    [templates]
  );

  const selectedTemplate = useMemo(
    () => approvedTemplates.find((t) => (t.templateId || t.id || t.name) === selectedTemplateId) || null,
    [approvedTemplates, selectedTemplateId]
  );

  useEffect(() => {
    if (!selectedTemplate) setTemplateFieldsComplete(false);
  }, [selectedTemplate]);

  const canProceed = Boolean(selectedGroup) && Boolean(selectedTemplate) && templateFieldsComplete;

  const recipients = selectedGroup?.contactCount || 0;

  // Creates the campaign on first save, updates it on every save after —
  // shared by the Save button and by Next (which must persist the campaign
  // before Send Broadcast can schedule/start something that actually exists).
  const persistCampaign = async () => {
    if (!selectedGroup || !selectedTemplate) return null;
    setSavingDraft(true);
    try {
      const payload = {
        campaignName: broadcastName,
        contactGroupId: selectedGroup._id,
        templateId: selectedTemplate.templateId || selectedTemplate.id || selectedTemplate.name,
        templateName: selectedTemplate.name,
        templateType: selectedTemplate.category,
        templateData: { language: selectedTemplate.language, components: selectedTemplate.components },
        customizations: buildCustomizations(templateVariables),
        label: broadcastLabel,
      };

      // A COMPLETED campaign can't be updated in place — the backend rejects
      // it, and messageStatuses were already built/consumed against its
      // original dataset/template. Continuing from here instead spins off a
      // brand-new draft with the current field values, stamped with today's
      // date if the name wasn't changed so it doesn't collide with the
      // original in the broadcast list.
      const isLockedCampaign = campaignId && campaignStatus === "COMPLETED";
      if (isLockedCampaign && payload.campaignName === existingCampaign?.campaignName) {
        payload.campaignName = `${payload.campaignName} - ${formatBroadcastDateSuffix(new Date())}`;
      }

      if (campaignId && !isLockedCampaign) {
        // Backend only allows this while the campaign is still PENDING/
        // SCHEDULED (a draft) — editing a campaign that's already started
        // fails with a clear error, surfaced below via the catch block.
        await dispatch(updateBulkMessageCampaign({ campaignId, ...payload })).unwrap();
        toast.success(`Draft "${broadcastName}" updated.`);
        return campaignId;
      }
      const result = await dispatch(createBulkMessageCampaign(payload)).unwrap();
      const newId = result?.data?.campaignId || null;
      setCampaignId(newId);
      setCampaignStatus("PENDING");
      if (isLockedCampaign) setBroadcastName(payload.campaignName);
      toast.success(
        isLockedCampaign
          ? `New broadcast "${payload.campaignName}" created.`
          : `Draft "${broadcastName}" saved.`
      );
      return newId;
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to save draft.");
      return null;
    } finally {
      setSavingDraft(false);
    }
  };

  const handleCancelBroadcast = async () => {
    if (!campaignId) return;
    setCancelling(true);
    try {
      const result = await dispatch(cancelBulkMessageCampaign({ campaignId })).unwrap();
      // Cancelling moves the campaign back to draft (PENDING) rather than a
      // terminal state, so stay on this page — content is editable again and
      // the broadcast can be sent/scheduled from here like any other draft.
      setCampaignStatus(result?.data?.status || "PENDING");
      toast.success("Broadcast moved to draft.");
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Failed to cancel broadcast.");
    } finally {
      setCancelling(false);
    }
  };

  const handleNext = async () => {
    // Once a campaign has actually started (IN_PROGRESS) or ended in
    // FAILED/CANCELLED, there's nothing sensible to persist or duplicate —
    // just reopen Send Broadcast (e.g. to cancel). A COMPLETED campaign is
    // the one locked status worth continuing from, so it falls through to
    // persistCampaign, which creates a new draft from the current field
    // values instead of updating the old one.
    if (campaignId && campaignStatus !== "COMPLETED" && !CONTENT_EDITABLE_STATUSES.has(campaignStatus)) {
      setSendModalOpen(true);
      return;
    }
    const id = await persistCampaign();
    if (id) setSendModalOpen(true);
  };

  const handleSegmentChange = (event) => {
    const value = event.target.value;
    if (value === ADD_DATASET_VALUE) {
      setAddingDataset(true);
      return;
    }
    setAddingDataset(false);
    setSelectedSegmentId(value);
  };

  const handleDatasetCreated = (newGroup) => {
    setSelectedSegmentId(newGroup._id);
    setAddingDataset(false);
  };

  // Includes the "Add Dataset" sentinel so every dropdown pick is a genuine
  // value change — otherwise re-picking the dataset that was selected before
  // "Add Dataset" was opened wouldn't fire onChange (MUI Select no-ops when
  // the value doesn't change), leaving the panel stuck on Add Dataset.
  const dropdownValue = addingDataset ? ADD_DATASET_VALUE : selectedSegmentId;

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Top bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          {editingName ? (
            <TextField
              size="small"
              autoFocus
              value={broadcastName}
              onChange={(e) => setBroadcastName(e.target.value)}
              onBlur={() => setEditingName(false)}
              onKeyDown={(e) => e.key === "Enter" && setEditingName(false)}
            />
          ) : (
            <>
              <Typography variant="subtitle1" fontWeight="bold">
                {broadcastName}
              </Typography>
              <IconButton size="small" onClick={() => setEditingName(true)}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {campaignId && CANCELLABLE_STATUSES.has(campaignStatus) && (
            <Button
              variant="outlined"
              color="error"
              sx={{ textTransform: "none" }}
              onClick={handleCancelBroadcast}
              disabled={cancelling}
            >
              {cancelling ? "Cancelling..." : "Cancel Broadcast"}
            </Button>
          )}
          <Button
            variant="outlined"
            sx={{ textTransform: "none" }}
            onClick={persistCampaign}
            disabled={
              !selectedGroup ||
              !selectedTemplate ||
              savingDraft ||
              (campaignId && !CONTENT_EDITABLE_STATUSES.has(campaignStatus))
            }
          >
            {savingDraft ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={handleNext}
            disabled={!canProceed || savingDraft}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
        {/* Configuration column */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: "auto",
            padding: 3,
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Configuration
          </Typography>

          {/* Dataset — the only functional field on this page for now */}
          <Box sx={{ mb: 3, mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2" fontWeight="500">
                Dataset
              </Typography>
              <Tooltip title="The dataset of contacts this broadcast will be sent to">
                <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              </Tooltip>
            </Box>
            <FormControl size="small" sx={{ width: 240 }}>
              <Select
                displayEmpty
                value={dropdownValue}
                onChange={handleSegmentChange}
                renderValue={(value) =>
                  value === ADD_DATASET_VALUE ? (
                    "Add Dataset"
                  ) : !value ? (
                    <Typography component="span" color="text.secondary">
                      Select a dataset
                    </Typography>
                  ) : (
                    selectedGroup?.name || ""
                  )
                }
              >
                {groups.map((group) => (
                  <MenuItem key={group._id} value={group._id}>
                    {group.name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value={ADD_DATASET_VALUE}>
                  <AddIcon sx={{ fontSize: 16, mr: 1 }} />
                  Add Dataset
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Message Content
          </Typography>
          {!addingMessage ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setAddingMessage(true)}
              sx={{ textTransform: "none", borderRadius: "2rem" }}
            >
              Add Message
            </Button>
          ) : approvedTemplates.length === 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No template exists
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`/home/${accountid}/templates`)}
                sx={{ textTransform: "none", borderRadius: "2rem" }}
              >
                Go to Templates
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" fontWeight="500" gutterBottom>
                Message Template
              </Typography>
              <FormControl size="small" sx={{ width: 240 }}>
                <Select
                  displayEmpty
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  renderValue={(value) =>
                    !value ? (
                      <Typography component="span" color="text.secondary">
                        Select a template
                      </Typography>
                    ) : (
                      selectedTemplate?.name || ""
                    )
                  }
                >
                  {approvedTemplates.map((t) => {
                    const value = t.templateId || t.id || t.name;
                    return (
                      <MenuItem key={value} value={value}>
                        {t.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {selectedTemplate && (
                <TemplateVariablesForm
                  template={selectedTemplate}
                  initialVariables={initialTemplateVariables}
                  onValidityChange={setTemplateFieldsComplete}
                  onVariablesChange={setTemplateVariables}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Right column — empty until a dataset is being added, a template is
            selected (preview takes priority), or a dataset is selected */}
        <Box
          sx={{
            width: 360,
            flexShrink: 0,
            overflowY: "auto",
            padding: addingDataset || selectedTemplate || selectedGroup ? 3 : 0,
            borderLeft: (theme) =>
              addingDataset || selectedTemplate || selectedGroup ? `1px solid ${theme.palette.divider}` : "none",
          }}
        >
          {addingDataset ? (
            <AddDatasetPanel onCancel={() => setAddingDataset(false)} onCreated={handleDatasetCreated} />
          ) : selectedTemplate ? (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Target Audience
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Sending a total of {recipients} message{recipients === 1 ? "" : "s"} to {recipients} recipient
                {recipients === 1 ? "" : "s"}
              </Typography>

              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Message Preview
              </Typography>
              <WhatsAppTemplatePreview
                template={selectedTemplate}
                headerPreviewUrl={templateVariables?.headerMedia?.mediaUrl}
                carouselCardsMedia={templateVariables?.carousel?.cards}
              />
            </>
          ) : selectedGroup ? (
            <EditDatasetPanel key={selectedGroup._id} group={selectedGroup} />
          ) : null}
        </Box>
      </Box>

      <SendBroadcastModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        recipients={recipients}
        campaignId={campaignId}
        initialScheduledAt={existingCampaign?.scheduledAt}
        initialTimezone={existingCampaign?.scheduledTimezone}
        onSent={() => navigate(-1)}
      />
    </Box>
  );
}

export default CreateBroadcastPage;
